<template>
  <div class="base-multi-select" ref="dropdown">
    <div class="base-multi-select__header" @click.stop="open()">
      <base-truncate-label v-if="truncateLabel"
                           class="base-multi-select__title"
                           :style="{ ...titleStyle, 'width': titleWidth}"
                           :label="selectTitle"
                           :shrink="1"
                           watch-class="base-multi-select__title" />
      <span v-else
            class="base-multi-select__title"
            :style="{ ...titleStyle, 'width': titleWidth}">
        {{ selectTitle }}
      </span>
      <span class="base-multi-select__arrow"
            :style="{'transform': isOpened ? 'rotate(180deg)' : 'none', 'top': isOpened ? '19px' : '16px'}"
            :class="{'base-multi-select__arrow_disabled': disabled}"></span>
    </div>
    <template v-if="isOpened">
      <div class="base-multi-select__overlay"
           @click.stop="close()"></div>
      <ul class="base-multi-select__list"
          :class="['base-multi-select__list_' + listPosition]"
          :style="listStyle">
        <li v-for="option in selectOptions"
            class="base-multi-select__item"
            :class="{ 'base-multi-select__item_selected': isSelected(option.value)}"
            @click.stop="onOptionSelected(option)" :key="option.value">
          <template v-if="useCheckBox">
            <span v-if="(!selectedValues.includes(option.value) || !useCrossWithCheckbox) && option.value !== 'remove'"
                  class="base-multi-select__checkbox-dummy"
                  :class="{'base-multi-select__checkbox-dummy_selected': selectedValues.includes(option.value)}"></span>
            <svg v-else class="base-multi-select__checkbox-cross" width="24" height="22" viewBox="0 0 20 20">
              <g fill="none" fill-rule="evenodd">
                <path d="M1 1L25 1 25 25 1 25z" transform="translate(-3 -3)"/>
                <path fill="#2f404e" fill-rule="nonzero"
                      d="M17.861 19.452L13 14.591l-4.861 4.861c-.39.39-1.024.39-1.415 0l-.176-.176c-.39-.391-.39-1.024 0-1.415L11.409 13 6.548 8.139c-.39-.39-.39-1.024 0-1.415l.176-.176c.391-.39 1.024-.39 1.415 0L13 11.409l4.861-4.861c.39-.39 1.024-.39 1.415 0l.176.176c.39.391.39 1.024 0 1.415L14.591 13l4.861 4.861c.39.39.39 1.024 0 1.415l-.176.176c-.391.39-1.024.39-1.415 0z"
                      transform="translate(-3 -3)"/>
              </g>
            </svg>
          </template>
          <base-truncate-label v-if="truncateLabel"
                               :style="{color: optionColor, ...(option.style || {})}"
                               class="base-multi-select__item-title"
                               v-bind:private-content="option.attributes?.some(attr=> attr['private-content'])"
                               :label="option.label"
                               :shrink="1"
                               watch-class="base-multi-select__item-title" />
          <span v-else
                class="base-multi-select__item-title"
                :style="{color: optionColor, ...(option.style || {})}">
            {{option.label}}
          </span>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
/* eslint-disable no-unused-vars */

import _ from 'lodash'
export default {
  name: 'BaseMultiSelect',
  model: {
    prop: 'modelValue',
    event: 'change'
  },
  props: {
    options: {
      type: Array,
      default: () => []
    },
    modelValue: {
      type: Array,
      default: () => []
    },
    alwaysEnabledColumns: {
      type: Array,
      default: () => []
    },
    defaultState: {
      type: Array,
      default: () => []
    },
    maxHeightList: {
      type: Number
    },
    // define label for "select all" option
    // array have to be as [unselected ALL label, selected ALL label]
    allLabel: {
      type: [String, Array],
      default: 'All'
    },
    optionColor: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    setDefaultOnAll: {
      type: Boolean,
      default: false
    },
    titleStyle: {
      type: Object,
      default: () => ({})
    },
    // show selected option by a checkbox. Default highlight view
    useCheckBox: {
      type: Boolean,
      default: false
    },
    // show cross when option selected, working when useCheckBox === true
    useCrossWithCheckbox: {
      type: Boolean,
      default: false
    },
    // disabling "select all" option
    disableAllOption: {
      type: Boolean,
      default: false
    },
    // property that defines showing a number of selected options, if selected only one option - shows option label
    countOptions: {
      type: Boolean,
      default: false
    },
    // using truncate logic for label
    truncateLabel: {
      type: Boolean,
      default: false
    },
    // enable "remove all" option, different with "select all" option, shows only <= 1 option selected
    enableRemoveOption: {
      type: Boolean,
      default: false
    },
    removeOptionLabel: {
      type: String,
      default: 'Remove Comparison'
    },
    // defines showing selected item, works if selected only 1 item, in another cases shows title
    showSelected: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      require: true
    }
  },
  mounted () {
    window.addEventListener('resize', _.debounce(() => {
      this.setListStyle()
    }, 50), false)
    this.manageRemoveOption()
    const ref = this.$refs.dropdown
    if (ref) {
      this.titleWidth = `${ref.clientWidth - 55}px`
    }
  },
  data () {
    return {
      isOpened: false,
      listStyle: {},
      listPosition: '',
      selectOptions: [],
      selectedValues: [],
      titleWidth: 'auto'
    }
  },
  watch: {
    options: {
      handler (v) {
        const optionValues = v.map(i => i.value)
        this.selectedValues = this.modelValue.filter(i => optionValues.includes(i))
        const selectedOptions = _.cloneDeep(v)
        if (!this.disableAllOption) {
          selectedOptions.unshift({ label: Array.isArray(this.allLabel) ? this.allLabel[this.allSelected ? 1 : 0] : this.allLabel, value: 'all' })
        }
        this.selectOptions = selectedOptions
      },
      immediate: true,
      deep: true
    },
    allSelected (v) {
      if (Array.isArray(this.allLabel) && !this.disableAllOption) {
        const allObj = this.selectOptions.find(i => i.value === 'all')
        if (allObj) {
          allObj.label = this.allLabel[v ? 1 : 0]
        }
      }
    },
    isOpened (v) {
      this.$emit(v ? 'open' : 'close')
    },
    modelValue (v) {
      this.selectedValues = v
    }
  },
  methods: {
    clear () {
      this.selectedValues = []
      this.$emit('change', [])
      this.close()
    },
    open () {
      this.isOpened = true
      this.setListStyle()
    },
    close () {
      this.isOpened = false
    },
    isSelected (v) {
      if (this.useCheckBox) {
        return false
      }
      return this.selectedValues.includes(v) || (v === 'all' && this.allSelected)
    },
    onOptionSelected (option) {
      if (option.value === 'all') {
        if (this.allSelected) {
          // clear all list
          this.selectedValues = this.setDefaultOnAll
            ? [...this.selectedValues.filter(i => !this.defaultState.includes(i))]
            : [...this.alwaysEnabledColumns]
        } else {
          // fill all list
          this.selectedValues = this.setDefaultOnAll
            ? [...this.defaultState]
            : [...this.alwaysEnabledColumns, ...this.options.map(i => i.value)]
        }
      } else if (option.value === 'remove') {
        this.selectedValues = [...this.alwaysEnabledColumns]
      } else if (this.selectedValues.includes(option.value) && !this.alwaysEnabledColumns.includes(option.value)) {
        // remove unselected option
        this.selectedValues.splice(this.selectedValues.indexOf(option.value), 1)
      } else {
        // add selected option
        this.selectedValues.push(option.value)
      }
      this.manageRemoveOption()
      this.$emit('change', this.selectedValues)
    },
    manageRemoveOption () {
      if (this.enableRemoveOption) {
        const values = this.selectedValues.filter(v => !this.alwaysEnabledColumns.includes(v))
        if (values.length >= 1) {
          if (!this.selectOptions.find(i => i.value === 'remove')) {
            this.selectOptions.unshift({ label: this.removeOptionLabel, value: 'remove' })
          }
        } else {
          this.selectOptions = this.selectOptions.filter(i => i.value !== 'remove')
        }
      }
    },
    setListStyle () {
      const ref = this.$refs['dropdown']
      if (!ref) {
        return
      }
      this.titleWidth = `${ref.clientWidth - 55}px`
      const style = {}
      const { top, bottom, left, right } = ref.getBoundingClientRect()
      const topShift = top
      const bottomShift = window.innerHeight - bottom
      let verticalProp, maxHeight
      if (topShift < bottomShift) {
        verticalProp = 'top'
        maxHeight = Math.round(bottomShift) - 30
      } else {
        verticalProp = 'bottom'
        maxHeight = Math.round(top) - 30
      }
      style[verticalProp] = '95%'
      maxHeight = this.maxHeightList ? this.maxHeightList : maxHeight
      style['max-height'] = `${maxHeight}px`
      if (verticalProp === 'top') {
        style['border-top'] = '2px solid rgb(40, 74, 255)'
        style['border-radius'] = '0 0 8px 8px'
      } else {
        style['border-bottom'] = '2px solid rgb(40, 74, 255)'
        style['border-radius'] = '8px 8px 0 0'
      }
      const leftShift = left
      const rightShift = window.innerWidth - right
      const horizontalProp = rightShift < leftShift ? 'right' : 'left'
      style[horizontalProp] = 0
      this.listStyle = style
      this.listPosition = verticalProp
    }
  },
  computed: {
    allSelected () {
      const options = (this.setDefaultOnAll ? this.defaultState : this.options.map(i => i.value)).sort()
      const modelValue = _.cloneDeep(this.selectedValues).sort()
      return _.difference(options, modelValue).length === 0
    },
    selectTitle () {
      return this.showSelected && this.selectedValues && this.selectedValues.length === 1
        ? (this.selectOptions.find(i => this.selectedValues.includes(i.value)) || { label: this.title }).label
        : this.countOptions && this.selectedValues && this.selectedValues.length >= 2
          ? `Selected (${this.selectedValues.length})`
          : this.title
    }
  }
}
</script>

<style lang="scss">
  .base-multi-select {
    position: relative;
    width: 190px;
    display: flex;
    min-width: 180px;

    &__title {
      color: $rfx-light-march;
      text-transform: uppercase;
      font-size: 12px;
      font-weight: 900;
      white-space: nowrap;
    }

    &__overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9998;
    }

    &__arrow {
      position: absolute;
      right: 15px;

      &::after {
        content: '';
        display: block;
        width: 7px;
        height: 7px;
        box-sizing: border-box;
        border-right: 2px solid $rfx-bright-blue;
        border-bottom: 2px solid $rfx-bright-blue;
        transform: rotate(45deg);
        transform-origin: center;
      }

      &_disabled {
        background-color: $rfx-almost-white-lighter;
        display: none;
      }
    }

    &__header {
      display: flex;
      flex-direction: column;
      width: 100%;
      justify-content: space-around;
      border-radius: 4px;
      border: solid 1px rgba(1, 22, 39, 0.1);
      padding: 0 15px;
      cursor: pointer;
      height: 40px;

      &:hover {
        background-color: rgba(40, 74, 255, .1);
      }
    }

    &__list {
      position: absolute;
      z-index: 9999;
      min-width: 100%;
      max-width: 600px;
      padding: 2px 0;
      background: #fff;
      list-style-type: none;
      overflow-x: hidden;
      overflow-y: auto;
      margin: 0 0 1px;
      box-sizing: border-box;

      &_top {
        box-shadow: 0 15px 40px -20px rgba(0, 0, 0, .3);
      }

      &_bottom {
        box-shadow: 0 -15px 40px -20px rgba(0, 0, 0, .3);
      }

      &__shadow {
        box-shadow: 0 0 1px 0 rgba(8, 8, 8, .24), 0 4px 15px 0 rgba(8, 8, 8, .24);
      }
    }

    &__checkbox-dummy {
      display: flex;
      width: 20px;
      height: 20px;
      flex: 0 0 20px;
      color: $rfx-silver;
      border: 2px solid currentColor;
      border-radius: 4px;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      background-color: $rfx-almost-white-lighter;
      margin-right: 0;

      &_selected {
        color: $rfx-bright-blue;
        background-color: currentColor;

        &::after {
          content: "";
          display: block;
          width: 9px;
          height: 5px;
          border-left: 2px solid #fff;
          border-bottom: 2px solid #fff;
          transform: rotate(-45deg) translateY(-1px);
          box-sizing: border-box;
        }
      }
    }

    &__item {
      height: 30px;
      line-height: 15px;
      padding: 5px 12px;
      display: flex;
      cursor: pointer;
      align-items: center;

      &:hover:not(.base-multi-select__item_selected) {
        background-color: rgba(40, 74, 255, .1);
        color: $rfx-dark-march;
      }

      &_selected {
        background-color: rgb(40, 74, 255);
        color: #fff;
      }

      &_disabled {
        pointer-events: none;
        color: $rfx-pearl;
        background-color: #fff;
      }
    }

    &__item-title {
      padding-left: 10px;
      @include ellipsis;
      font-size: 12px;
      white-space: initial;
      user-select: none;
      flex: 1 1 100%;

    }
  }

</style>
